import React, { useMemo } from "react";

import { useBusyBlockerContext } from "../../../contexts/busy-blocker-context";
import { useErrorHandling } from "../../../hooks/useErrorHandling";
import { arrayComparer, useMessageToState } from "../../../hooks/useMessageToState";
import { useSelectedZone } from "../../../hooks/useSelection";
import { useTextZoneDashboard } from "../../../hooks/useTextZoneDashboard";
import { isTextAlignmentPanelSupported } from "./menu-info";
import { TextAlignmentPanel } from "./text-alignment-panel";
import { useDefinition } from "../../../hooks/useDefinition";
import { useAllowedTextZoneAlignments } from "../../../hooks/use-allowed-textzone-alignments";

export const useTextZoneAlignment = (textZone: eXpress.core.AnyTextZone | undefined) => {
	return useMessageToState<
		readonly [
			horizontalAlignment: eXpress.core.TextAlignment,
			verticalAlignment: eXpress.core.TextAlignment,
		]
	>(
		useMemo(
			() => ({
				name: "useTextZoneAlignment",
				filterMessage: (
					message, // State can change when:
				) =>
					eXpress.core.isSelectedZoneChanged(message) || // other zone is selected
					eXpress.core.isZoneAffected(message, textZone), // textZone is changed
				getState: () => {
					return textZone
						? ([
								textZone.textSettings.horizontalAlignment,
								textZone.textSettings.verticalAlignment,
						  ] as const)
						: ([
								eXpress.core.TextAlignment.Near,
								eXpress.core.TextAlignment.Near,
						  ] as const);
				},
				comparer: arrayComparer,
			}),
			[textZone],
		),
	);
};

export const TextAlignmentMenuContainer = () => {
	const textDashboard = useTextZoneDashboard();
	const selectedZone = useSelectedZone();
	const { busyBlocker } = useBusyBlockerContext();
	const { createErrorHandler } = useErrorHandling();
	const definition = useDefinition();

	const textZone = eXpress.core.isAnyTextZone(selectedZone) ? selectedZone : undefined;

	const [horizontalAlignment, verticalAlignment] = useTextZoneAlignment(textZone);

	const isTextAlignmentEnabled = isTextAlignmentPanelSupported(
		selectedZone,
		definition,
		textDashboard,
	);

	const {
		canSetHorizontalAlignmentToNear,
		canSetHorizontalAlignmentToCenter,
		canSetHorizontalAlignmentToFar,
		canSetVerticalAlignmentToNear,
		canSetVerticalAlignmentToCenter,
		canSetVerticalAlignmentToFar,
	} = useAllowedTextZoneAlignments();

	return isTextAlignmentEnabled ? (
		<TextAlignmentPanel
			canSetHorizontalAlignmentToNear={canSetHorizontalAlignmentToNear}
			canSetHorizontalAlignmentToCenter={canSetHorizontalAlignmentToCenter}
			canSetHorizontalAlignmentToFar={canSetHorizontalAlignmentToFar}
			canSetVerticalAlignmentToNear={canSetVerticalAlignmentToNear}
			canSetVerticalAlignmentToCenter={canSetVerticalAlignmentToCenter}
			canSetVerticalAlignmentToFar={canSetVerticalAlignmentToFar}
			selectedHorizontalAlignment={horizontalAlignment}
			selectedVerticalAlignment={verticalAlignment}
			setHorizontalAlignment={(alignment) => {
				if (!textZone) return;
				busyBlocker
					.during(textDashboard.setHorizontalAlignmentAsync(textZone, alignment))
					.catch(createErrorHandler());
			}}
			setVerticalAlignment={(alignment) => {
				if (!textZone) return;
				busyBlocker
					.during(textDashboard.setVerticalAlignmentAsync(textZone, alignment))
					.catch(createErrorHandler());
			}}
		/>
	) : null;
};
TextAlignmentMenuContainer.displayName = "TextAlignmentMenuContainer";
